/**
 * V2 tool button that properly displays custom names (e.g., "QueryDatadogLogs")
 * Mimics v1 ToolCallButton styling but with v2 display names
 * Supports approval UI for tools requiring user consent
 */

import { Ban, Check, Clock, SquareCheck, X, XCircle } from "lucide-react";
import * as React from "react";
import { graphql, useFragment } from "react-relay";
import { Button } from "src/components/ui/button";
import { LoadingSpinner } from "src/components/ui/loading-spinner";

import { V2ToolButton_toolCall$key } from "./__generated__/V2ToolButton_toolCall.graphql";
import { extractToolCustomInfo,getV2ToolDisplayName } from "./v2Utils";

interface V2ToolButtonProps {
  toolCall: V2ToolButton_toolCall$key;
  onClick?: () => void;
  isDisabled?: boolean;
  isPending?: boolean;
  isWaiting?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
  approvalInFlight?: boolean;
}

const fragment = graphql`
  fragment V2ToolButton_toolCall on GraphQLMessageToolCall {
    toolUse {
      name
      displayName
      input {
        __typename
        ... on GenericToolInput {
          rawInput
        }
      }
    }
    toolResult {
      hasData
      commandFailed
      content
    }
  }
`;

export const V2ToolButton: React.FC<V2ToolButtonProps> = ({
  toolCall: toolCallKey,
  onClick,
  isDisabled = false,
  isPending = false,
  isWaiting = false,
  onApprove,
  onReject,
  approvalInFlight = false,
}) => {
  const data = useFragment(fragment, toolCallKey);

  const toolName = data.toolUse?.name || "";
  const rawInput = data.toolUse?.input?.__typename === "GenericToolInput" ? data.toolUse.input.rawInput : null;
  const displayName = getV2ToolDisplayName(toolName, rawInput);
  const customInfo = extractToolCustomInfo({ toolUse: data.toolUse } as any);

  // Check if tool has completed
  const isLoading = !data.toolResult || data.toolResult?.hasData === null;
  const hasData = data.toolResult?.hasData;
  const commandFailed = data.toolResult?.commandFailed;

  // If waiting (no approval needed, will run after approvals), show greyed out
  if (isWaiting) {
    return (
      <button
        onClick={onClick}
        className="flex flex-wrap items-center gap-x-2 gap-y-1 bg-popover text-gray-500 text-xs rounded px-3 py-1.5 border border-border text-left w-fit cursor-default opacity-60"
      >
        <Clock className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="font-semibold">{displayName}</span>
        {customInfo && (
          <>
            <span className="text-gray-600">|</span>
            <span className="text-gray-600 break-all">{customInfo}</span>
          </>
        )}
      </button>
    );
  }

  // If pending approval, show amber-themed approval UI with inline buttons
  if (isPending) {
    return (
      <div className="flex flex-wrap items-center gap-2 bg-amber-950/20 hover:bg-amber-950/30 text-gray-300 text-xs rounded px-3 py-2 border border-amber-700/40 w-fit">
        <button onClick={onClick} className="flex flex-wrap items-center gap-x-2 gap-y-1 flex-1 min-w-0">
          <Clock className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />
          <span className="font-semibold">{displayName}</span>
          <span className="text-gray-500">|</span>
          <span className="text-amber-300">Requires approval (Click to see parameters)</span>
        </button>
        <div className="flex gap-1.5 pl-2 border-l border-amber-700/40">
          <Button
            size="sm"
            variant="default"
            onClick={(e) => {
              e.stopPropagation();
              onApprove?.();
            }}
            disabled={approvalInFlight}
            className="h-7 px-2 flex items-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {approvalInFlight ? (
              <LoadingSpinner className="h-3.5 w-3.5" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onReject?.();
            }}
            disabled={approvalInFlight}
            className="h-7 px-2 flex items-center gap-1.5 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {approvalInFlight ? (
              <LoadingSpinner className="h-3.5 w-3.5" />
            ) : (
              <X className="h-3.5 w-3.5" />
            )}
            Reject
          </Button>
        </div>
      </div>
    );
  }

  // Build border classes with shimmer effect - shimmer when loading (in progress)
  const borderClasses = isLoading ? "border-border animate-pulse" : "border-border";

  // Default state - normal tool button
  return (
    <button
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      className={`flex flex-wrap items-center gap-x-2 gap-y-1 bg-muted text-foreground text-xs rounded px-3 py-1.5 border text-left w-fit transition-colors ${borderClasses} ${
        isDisabled ? "cursor-not-allowed opacity-60" : "hover:bg-accent cursor-pointer"
      }`}
    >
      {isLoading ? (
        <LoadingSpinner className="h-3.5 w-3.5 flex-shrink-0" />
      ) : commandFailed ? (
        <XCircle className="h-3.5 w-3.5 text-red-400 flex-shrink-0" aria-label="Command failed" />
      ) : hasData ? (
        <SquareCheck className="h-3.5 w-3.5 text-green-400 flex-shrink-0" />
      ) : (
        <Ban className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
      )}
      <span className="font-semibold">{displayName}</span>
      {customInfo && (
        <>
          <span className="text-muted-foreground">|</span>
          <span className="text-muted-foreground break-all">{customInfo}</span>
        </>
      )}
    </button>
  );
};
